/**
 * FloatingChatButtons — polished bottom-right pair of round chat buttons.
 * Deep brand-tinted glass with a soft radial glow, white icon glyphs and a
 * coloured drop-shadow. Icon-only, no outer chrome — premium fintech feel.
 */
import React, { useState } from 'react';

const WA_URL = 'https://wa.me/447700183957';
const TG_URL = 'https://t.me/B2BHub_inbox_bot';

function RoundButton({ href, gradient, glow, ring, icon, testid, label }) {
  const [hover, setHover] = useState(false);
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      data-testid={testid}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: 'relative',
        width: '56px',
        height: '56px',
        borderRadius: '999px',
        background: gradient,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        textDecoration: 'none',
        color: '#ffffff',
        // Colored drop shadow + subtle inner highlight ring for gloss
        boxShadow: `
          0 12px 28px -8px ${glow},
          0 4px 10px -2px rgba(0,0,0,0.35),
          inset 0 1px 0 rgba(255,255,255,0.22),
          inset 0 0 0 1px ${ring}
        `,
        transition: 'transform 220ms cubic-bezier(.4,0,.2,1), box-shadow 220ms',
        transform: hover ? 'translateY(-3px) scale(1.06)' : 'translateY(0) scale(1)',
      }}
    >
      {icon}
      {/* Specular gloss overlay */}
      <span
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          borderRadius: '999px',
          background:
            'radial-gradient(120% 80% at 30% 0%, rgba(255,255,255,0.28) 0%, rgba(255,255,255,0.06) 40%, transparent 65%)',
          pointerEvents: 'none',
        }}
      />
    </a>
  );
}

/* Clean WhatsApp glyph (no outer circle — the button provides the circle) */
const WhatsAppIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" style={{ position: 'relative', zIndex: 1 }}>
    <path d="M20.52 3.48A11.86 11.86 0 0 0 12.02 0C5.48 0 .17 5.31.17 11.85c0 2.09.55 4.13 1.6 5.93L0 24l6.4-1.68a11.83 11.83 0 0 0 5.62 1.43h.01c6.54 0 11.85-5.31 11.85-11.85 0-3.17-1.23-6.15-3.36-8.42zM12.02 21.8h-.01a9.94 9.94 0 0 1-5.07-1.39l-.36-.22-3.79.99 1.01-3.7-.24-.38a9.9 9.9 0 0 1-1.53-5.25c0-5.47 4.45-9.93 9.93-9.93 2.65 0 5.14 1.03 7.02 2.91a9.86 9.86 0 0 1 2.9 7.02c0 5.48-4.45 9.95-9.86 9.95zm5.44-7.44c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.16-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51l-.57-.01c-.2 0-.52.07-.79.37s-1.04 1.02-1.04 2.48c0 1.46 1.07 2.88 1.22 3.08.15.2 2.1 3.2 5.09 4.49.71.31 1.27.49 1.7.63.71.22 1.36.19 1.87.12.57-.09 1.76-.72 2-1.42.25-.7.25-1.29.17-1.42-.07-.12-.27-.2-.57-.35z"/>
  </svg>
);

/* Clean Telegram plane glyph */
const TelegramIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" style={{ position: 'relative', zIndex: 1, marginLeft: '-2px' }}>
    <path d="M21.94 4.26c-.28-.12-.63-.06-.86.15L2.61 10.57c-.37.13-.61.48-.61.88 0 .39.24.74.61.88l4.4 1.56 1.7 5.36c.08.24.28.42.53.46l.1.01c.22 0 .43-.1.57-.28l2.5-3.13 4.63 3.32c.14.1.31.16.48.16.09 0 .18-.02.27-.05.24-.1.42-.32.47-.58l3.06-13.81c.07-.31-.06-.62-.32-.79zm-3.38 3.62-7.98 7.02-.24 3.1-1.24-3.9 8.78-6.86c.19-.15.46-.14.63.02.17.16.19.42.05.62z"/>
  </svg>
);

export default function FloatingChatButtons() {
  return (
    <div
      data-testid="floating-chat-buttons"
      style={{
        position: 'fixed',
        right: '22px',
        bottom: '22px',
        display: 'flex',
        flexDirection: 'column',
        gap: '14px',
        zIndex: 9999,
        pointerEvents: 'auto',
      }}
    >
      <RoundButton
        href={WA_URL}
        gradient="linear-gradient(140deg, #34E17F 0%, #1FBA5B 55%, #128A44 100%)"
        glow="rgba(37,211,102,0.55)"
        ring="rgba(255,255,255,0.10)"
        icon={WhatsAppIcon}
        testid="floating-chat-whatsapp"
        label="Chat on WhatsApp"
      />
      <RoundButton
        href={TG_URL}
        gradient="linear-gradient(140deg, #3BB6EE 0%, #229ED9 55%, #147EB3 100%)"
        glow="rgba(34,158,217,0.55)"
        ring="rgba(255,255,255,0.10)"
        icon={TelegramIcon}
        testid="floating-chat-telegram"
        label="Chat on Telegram"
      />
    </div>
  );
}
